import TruncatedTypography from '@components/TruncatedTypography';
import { ROW_HOVER_BG } from '@features/reports2/colors';
import { msg, Plural, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  TextField,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { List, RowComponentProps } from 'react-window';
import { InnerPaper } from '../common';

interface Props<T, U> {
  title: ReactNode;
  options: {
    label: string;
    value: T;
    checked: boolean;
    select?: {
      options: { label: string; value: U }[];
      value: U | null;
      onChange: (value: U, idx: number) => void;
    };
  }[];
  onToggle: (value: T, checked: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  selectAllDisabled: boolean;
  deselectAllDisabled: boolean;
  search: string;
  onSearch: (value: string) => void;
  selectedLength: number;
}

const ROW_HEIGHT = 40;

export const MultipleCheckbox = <T, U>(props: Props<T, U>) => {
  const { _ } = useLingui();

  return (
    <InnerPaper>
      <Box display="flex" gap={2} justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontWeight="bold">{props.title}</Typography>
        <Typography>
          <Plural value={props.selectedLength} one="# seleccionado" other="# seleccionados" />
        </Typography>
      </Box>
      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <TextField
          onChange={(e) => props.onSearch(e.target.value)}
          value={props.search}
          label={_(msg`Buscar`)}
          size="small"
          sx={{
            flex: 1,
          }}
        />
        <Button
          variant="contained"
          color="info"
          onClick={props.onSelectAll}
          disabled={props.selectAllDisabled}
        >
          <Trans>Todos</Trans>
        </Button>
        <Button
          variant="outlined"
          color="info"
          onClick={props.onDeselectAll}
          disabled={props.deselectAllDisabled}
        >
          <Trans>Ninguno</Trans>
        </Button>
      </Box>
      <Box
        sx={{
          height: 'max(calc(100dvh - 480px), 200px)',
        }}
      >
        {props.options.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic" p={2}>
            <Trans>Selecciona un dispositivo para mostrar sus métricas</Trans>
          </Typography>
        ) : (
          <List
            rowComponent={ListButton}
            rowHeight={ROW_HEIGHT}
            rowCount={props.options.length}
            rowProps={props}
            style={{ scrollbarWidth: 'thin' }}
          />
        )}
      </Box>
    </InnerPaper>
  );
};

const ListButton = <T, U>({
  index,
  style,
  ariaAttributes,
  options,
  onToggle,
}: RowComponentProps<Props<T, U>>) => {
  const { label, value, checked, select } = options[index];
  return (
    <div style={style} {...ariaAttributes}>
      <ButtonBase
        onClick={() => onToggle(value, !checked)}
        sx={{
          px: 1,
          height: ROW_HEIGHT,
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          ':hover': {
            bgcolor: ROW_HOVER_BG,
          },
          gap: 2,
        }}
      >
        <Checkbox checked={checked} tabIndex={-1} disableRipple sx={{ p: 0 }} size="small" />
        {select && (
          <Autocomplete
            size="small"
            value={select.value ?? undefined}
            onChange={(e, value) => {
              e.stopPropagation();
              if (value) {
                select.onChange(value, index);
              }
            }}
            getOptionLabel={(value) => {
              return (
                select.options?.find((option) => option.value === value)?.label ?? String(value)
              );
            }}
            options={select.options.map((option) => option.value)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                onMouseOver={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  ...params.InputProps,
                  sx: { height: 34, width: 120 },
                }}
              />
            )}
            onMouseOver={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <TruncatedTypography>{label}</TruncatedTypography>
      </ButtonBase>
    </div>
  );
};
